import { supabase } from '../lib/supabase';

const centsToDollars = (cents = 0) => cents / 100;

const AI_MATCH_BATCH_SIZE = 50;
const AI_MIN_CONFIDENCE = 0.8;

const ASSESSMENT_ALIASES = [
  'assessment_code', 'assessment', 'parcel_id', 'parcel', 'parcel_number',
  'account', 'account_number', 'account_no', 'acct', 'acct_no', 'property_code',
];

const ADDRESS_ALIASES = [
  'property_address', 'situs_address', 'service_address', 'street_address',
  'address', 'addr', 'location', 'property', 'unit_address', 'premises',
];

const OWNER_ALIASES = [
  'owner_name', 'owner', 'customer', 'customer_name', 'customer_full_name',
  'name', 'display_name',
];

const BALANCE_ALIASES = [
  'open_balance', 'balance', 'balance_total', 'total_balance', 'current_balance',
  'amount_due', 'open_amount', 'past_due', 'dues_balance', 'balance_due', 'total',
  'remaining_balance', 'amount',
];

// QuickBooks A/R Aging Summary bucket headers after normalizeHeader():
// "Current" -> current, "1 - 30" -> 1_30, "> 90" -> 90, "91 and over" -> 91_and_over
const AGING_CURRENT_ALIASES = ['current'];
const AGING_1_30_ALIASES = ['1_30', '1_30_days', '1_to_30'];
const AGING_31_60_ALIASES = ['31_60', '31_60_days', '31_to_60'];
const AGING_61_90_ALIASES = ['61_90', '61_90_days', '61_to_90'];
const AGING_OVER_90_ALIASES = ['90', 'over_90', '90_plus', '91_and_over', '_90', 'gt_90'];

const DATE_ALIASES = [
  'as_of_date', 'as_of', 'date', 'statement_date', 'report_date', 'balance_date',
];

const NOTE_ALIASES = ['notes', 'note', 'memo', 'description'];

const normalizeHeader = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const normalizeAddress = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/\b(north)\b/g, 'n')
  .replace(/\b(south)\b/g, 's')
  .replace(/\b(east)\b/g, 'e')
  .replace(/\b(west)\b/g, 'w')
  .replace(/\b(road)\b/g, 'rd')
  .replace(/\b(street)\b/g, 'st')
  .replace(/\b(avenue)\b/g, 'ave')
  .replace(/\b(drive)\b/g, 'dr')
  .replace(/\b(lane)\b/g, 'ln')
  .replace(/\b(court)\b/g, 'ct')
  .replace(/\b(circle)\b/g, 'cir')
  .replace(/\b(place)\b/g, 'pl')
  .replace(/\b(boulevard)\b/g, 'blvd')
  .replace(/\b(highway)\b/g, 'hwy')
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const normalizeLookupKey = (value) => String(value || '').trim().toLowerCase();

// Canonical key for statement customer names: case, punctuation, and
// whitespace insensitive so "Smith, John" and "SMITH JOHN" share one mapping.
export const normalizeOwnerName = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const parseCsvRows = (text) => {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (char === '"' && inQuotes && next === '"') {
      cell += '"';
      i += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (char === ',' && !inQuotes) {
      row.push(cell.trim());
      cell = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') i += 1;
      row.push(cell.trim());
      if (row.some((value) => value !== '')) rows.push(row);
      row = [];
      cell = '';
      continue;
    }

    cell += char;
  }

  row.push(cell.trim());
  if (row.some((value) => value !== '')) rows.push(row);
  return rows;
};

const pickValue = (row, aliases) => {
  for (const alias of aliases) {
    const value = row[alias];
    if (value !== undefined && String(value).trim() !== '') return value;
  }
  return '';
};

const parseCurrencyToCents = (value) => {
  let normalized = String(value || '').trim();
  if (!normalized) return null;

  const isParentheticalCredit = /^\(.*\)$/.test(normalized);
  const hasTrailingMinus = /-$/.test(normalized);
  normalized = normalized
    .replace(/[,$\s]/g, '')
    .replace(/[()]/g, '')
    .replace(/-$/g, '');

  if (!normalized) return null;
  const amount = Number(normalized);
  if (!Number.isFinite(amount)) return null;
  const signedAmount = isParentheticalCredit || hasTrailingMinus ? -Math.abs(amount) : amount;
  return Math.round(signedAmount * 100);
};

const parseAsOfDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
};

// QuickBooks A/R Aging Summary exports include a grand TOTAL row and
// occasionally section subtotal rows. Those are report artifacts, not owners.
const isReportArtifactRow = (name) => /^total\b/i.test(String(name || '').trim());

const addToIndex = (index, key, property) => {
  if (!key) return;
  const current = index.get(key) || [];
  current.push(property);
  index.set(key, current);
};

const getUniqueCandidate = (index, key) => {
  if (!key) return { state: 'missing', property: null };
  const matches = index.get(key) || [];
  if (matches.length === 0) return { state: 'none', property: null };
  if (matches.length === 1) return { state: 'unique', property: matches[0] };
  return { state: 'ambiguous', property: null };
};

const buildPropertyIndexes = (properties = []) => {
  const byAssessment = new Map();
  const byAddress = new Map();
  const byId = new Map();

  properties.forEach((property) => {
    addToIndex(byAssessment, normalizeLookupKey(property.assessment_code), property);
    addToIndex(byAddress, normalizeAddress(property.situs_address), property);
    byId.set(property.id, property);
  });

  return { byAssessment, byAddress, byId };
};

const loadBalanceImportProperties = async (hoaId) => {
  try {
    const { data, error } = await supabase.rpc('get_balance_import_properties', { p_hoa_id: hoaId });
    if (error) throw error;
    if (Array.isArray(data)) return data;
  } catch (error) {
    console.warn('Balance import RPC unavailable, falling back to properties table:', error?.message || error);
  }

  const { data, error } = await supabase
    .from('properties')
    .select('id, assessment_code, situs_address, owner_name_legal, block, lot')
    .eq('hoa_id', hoaId)
    .eq('membership_status', 'active')
    .range(0, 999);

  if (error) throw error;
  return data || [];
};

// Persistent memory: confirmed statement-name -> property mappings. Once a
// name is confirmed it auto-resolves on every future upload without AI.
const loadNameMappings = async (hoaId) => {
  try {
    const { data, error } = await supabase
      .from('balance_name_mappings')
      .select('id, source_name, normalized_name, property_id, match_source, confidence')
      .eq('hoa_id', hoaId)
      .range(0, 1999);

    if (error) throw error;
    return new Map((data || []).map((mapping) => [mapping.normalized_name, mapping]));
  } catch (error) {
    console.warn('Name mapping memory unavailable:', error?.message || error);
    return new Map();
  }
};

const requestAiNameMatches = async ({ hoaId, names }) => {
  const results = new Map();

  for (let start = 0; start < names.length; start += AI_MATCH_BATCH_SIZE) {
    const batch = names.slice(start, start + AI_MATCH_BATCH_SIZE);
    const { data, error } = await supabase.functions.invoke('match-balance-names', {
      body: { hoa_id: hoaId, names: batch },
    });

    if (error) throw new Error(error.message || 'AI name matching failed.');
    if (data?.error) throw new Error(data.error);

    (data?.matches || []).forEach((match) => {
      if (match?.qb_name) results.set(match.qb_name, match);
    });
  }

  return results;
};

const buildAgingFromRecord = (record) => {
  const buckets = {
    agingCurrentCents: parseCurrencyToCents(pickValue(record, AGING_CURRENT_ALIASES)),
    aging1To30Cents: parseCurrencyToCents(pickValue(record, AGING_1_30_ALIASES)),
    aging31To60Cents: parseCurrencyToCents(pickValue(record, AGING_31_60_ALIASES)),
    aging61To90Cents: parseCurrencyToCents(pickValue(record, AGING_61_90_ALIASES)),
    agingOver90Cents: parseCurrencyToCents(pickValue(record, AGING_OVER_90_ALIASES)),
  };
  const hasAging = Object.values(buckets).some((value) => value !== null);
  return { ...buckets, hasAging };
};

const sumAgingCents = (aging) => (
  (aging.agingCurrentCents || 0)
  + (aging.aging1To30Cents || 0)
  + (aging.aging31To60Cents || 0)
  + (aging.aging61To90Cents || 0)
  + (aging.agingOver90Cents || 0)
);

const toPropertySummary = (property) => (property ? {
  propertyId: property.id,
  propertyAddress: property.situs_address,
  ownerName: property.owner_name_legal,
  assessmentCode: property.assessment_code,
  block: property.block,
  lot: property.lot,
} : {});

export const getPaymentPortalUrl = async (hoaId) => {
  try {
    const { data, error } = await supabase.rpc('get_payment_portal_url', { p_hoa_id: hoaId });
    if (error) throw error;
    if (typeof data === 'string') return data;
  } catch (error) {
    console.warn('Payment portal RPC unavailable, falling back to HOA row:', error?.message || error);
  }

  try {
    const { data, error } = await supabase
      .from('hoas')
      .select('payment_portal_url, settings')
      .eq('id', hoaId)
      .maybeSingle();

    if (error) throw error;
    return data?.payment_portal_url || data?.settings?.payment_portal_url || '';
  } catch (error) {
    console.warn('Payment portal URL unavailable:', error?.message || error);
    return '';
  }
};

export const updatePaymentPortalUrl = async ({ hoaId, paymentPortalUrl }) => {
  if (!hoaId) throw new Error('Missing HOA id.');

  const trimmedUrl = String(paymentPortalUrl || '').trim();
  if (trimmedUrl && !/^https:\/\//i.test(trimmedUrl)) {
    throw new Error('Use a secure https payment link.');
  }

  const { data, error } = await supabase.rpc('update_payment_portal_url', {
    p_hoa_id: hoaId,
    p_payment_portal_url: trimmedUrl || null,
  });

  if (error) throw error;
  return typeof data === 'string' ? data : trimmedUrl;
};

export const getVisibleDocumentCount = async ({ hoaId, role = 'member' }) => {
  if (!hoaId) return 0;

  let query = supabase
    .from('documents')
    .select('id', { count: 'exact', head: true })
    .eq('hoa_id', hoaId);

  if (role === 'member') {
    query = query.eq('visible_to_homeowners', true);
  }

  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
};

export const getNextVisibleEvent = async ({ hoaId }) => {
  if (!hoaId) return null;

  const { data, error } = await supabase
    .from('board_meetings')
    .select('id, title, meeting_type, scheduled_at, location, meeting_url, visible_to_roles')
    .eq('hoa_id', hoaId)
    .gte('scheduled_at', new Date().toISOString())
    .order('scheduled_at', { ascending: true })
    .limit(1);

  if (error) throw error;
  return data?.[0] || null;
};

export const getCurrentUserBalance = async ({ hoaId, userId }) => {
  const emptyAging = {
    agingCurrentCents: 0,
    agingPastDue1To30Cents: 0,
    agingPastDue31PlusCents: 0,
    hasAgingDetail: false,
    hasPastDue31Plus: false,
  };

  if (!hoaId || !userId) {
    return {
      totalCents: 0, total: 0, properties: [], paymentPortalUrl: '', asOfDate: null,
      hasBalanceData: false, ...emptyAging,
    };
  }

  const { data: occupants, error: occupantError } = await supabase
    .from('property_occupants')
    .select('property_id, is_primary_contact')
    .eq('hoa_id', hoaId)
    .eq('user_id', userId)
    .eq('is_active', true);

  if (occupantError) throw occupantError;

  const propertyRows = (occupants || [])
    .map((row) => ({ id: row.property_id, is_primary_contact: row.is_primary_contact }))
    .filter((property) => property.id);
  const propertyIds = propertyRows.map((property) => property.id);
  const paymentPortalUrl = await getPaymentPortalUrl(hoaId);

  if (propertyIds.length === 0) {
    return {
      totalCents: 0, total: 0, properties: [], paymentPortalUrl, asOfDate: null,
      hasBalanceData: false, ...emptyAging,
    };
  }

  let balances = [];
  try {
    const { data, error: balanceError } = await supabase
      .from('property_balances')
      .select('property_id, balance_cents, as_of_date, notes, updated_at, aging_current_cents, aging_1_30_cents, aging_31_60_cents, aging_61_90_cents, aging_over_90_cents')
      .eq('hoa_id', hoaId)
      .in('property_id', propertyIds);

    if (balanceError) throw balanceError;
    balances = data || [];
  } catch (error) {
    console.warn('Property balances unavailable:', error?.message || error);
  }

  const balanceByProperty = new Map((balances || []).map((balance) => [balance.property_id, balance]));
  const properties = propertyRows.map((property) => {
    const balance = balanceByProperty.get(property.id);
    const balanceCents = balance?.balance_cents || 0;
    return {
      ...property,
      balanceCents,
      balance: centsToDollars(balanceCents),
      asOfDate: balance?.as_of_date || null,
      notes: balance?.notes || '',
    };
  });
  const totalCents = properties.reduce((sum, property) => sum + property.balanceCents, 0);
  const asOfDates = properties
    .map((property) => property.asOfDate)
    .filter(Boolean)
    .sort();
  const asOfDate = asOfDates.length > 0 ? asOfDates[asOfDates.length - 1] : null;

  const hasAgingDetail = balances.some((balance) => (
    balance.aging_current_cents !== null
    || balance.aging_1_30_cents !== null
    || balance.aging_31_60_cents !== null
    || balance.aging_61_90_cents !== null
    || balance.aging_over_90_cents !== null
  ));
  const agingCurrentCents = balances.reduce((sum, b) => sum + (b.aging_current_cents || 0), 0);
  const agingPastDue1To30Cents = balances.reduce((sum, b) => sum + (b.aging_1_30_cents || 0), 0);
  const agingPastDue31PlusCents = balances.reduce((sum, b) => (
    sum + (b.aging_31_60_cents || 0) + (b.aging_61_90_cents || 0) + (b.aging_over_90_cents || 0)
  ), 0);

  return {
    totalCents,
    total: centsToDollars(totalCents),
    properties,
    paymentPortalUrl,
    asOfDate,
    hasBalanceData: balances.length > 0,
    agingCurrentCents,
    agingPastDue1To30Cents,
    agingPastDue31PlusCents,
    hasAgingDetail,
    hasPastDue31Plus: agingPastDue31PlusCents > 0,
  };
};

// Reviews a balance CSV against three matching layers, in order:
// 1. Deterministic: assessment code or exact address (no AI, no review needed)
// 2. Memory: previously confirmed statement-name mappings (no AI, no review needed)
// 3. AI: Claude proposes matches for leftover names (admin must confirm)
// Anything still unmatched lands in attentionRows for manual assignment.
export const analyzePropertyBalancesCsv = async ({ hoaId, file, asOfDate }) => {
  if (!hoaId) throw new Error('Missing HOA id.');
  if (!file) throw new Error('Choose a CSV file to review.');

  const fallbackAsOfDate = parseAsOfDate(asOfDate);
  if (!fallbackAsOfDate) throw new Error('Choose the statement date for this balance file.');

  const text = await file.text();
  const rows = parseCsvRows(text);
  if (rows.length < 2) throw new Error('CSV must include a header row and at least one balance row.');

  const headers = rows[0].map(normalizeHeader);
  const records = rows.slice(1).map((sourceRow) => headers.reduce((acc, header, index) => {
    acc[header] = sourceRow[index] || '';
    return acc;
  }, {}));

  const [properties, nameMappings] = await Promise.all([
    loadBalanceImportProperties(hoaId),
    loadNameMappings(hoaId),
  ]);
  const { byAssessment, byAddress, byId } = buildPropertyIndexes(properties);

  const readyRows = [];
  const aiPendingRows = [];
  const attentionRows = [];
  const invalidRows = [];
  let skippedArtifactRows = 0;

  records.forEach((record, index) => {
    const rowNumber = index + 2;
    const sourceAssessmentCode = normalizeLookupKey(pickValue(record, ASSESSMENT_ALIASES));
    const sourceAddress = pickValue(record, ADDRESS_ALIASES);
    // QuickBooks A/R exports often leave the customer column header blank.
    const sourceOwnerName = pickValue(record, OWNER_ALIASES) || String(record[''] || '').trim();
    const aging = buildAgingFromRecord(record);
    const balanceValue = pickValue(record, BALANCE_ALIASES);
    let balanceCents = parseCurrencyToCents(balanceValue);
    if (balanceCents === null && aging.hasAging) {
      balanceCents = sumAgingCents(aging);
    }
    const rowAsOfDate = parseAsOfDate(pickValue(record, DATE_ALIASES)) || fallbackAsOfDate;
    const notes = pickValue(record, NOTE_ALIASES) || null;

    if (isReportArtifactRow(sourceOwnerName)) {
      skippedArtifactRows += 1;
      return;
    }

    const baseRow = {
      rowNumber,
      sourceAssessmentCode,
      sourceAddress,
      sourceOwnerName,
      balanceCents,
      balance: centsToDollars(balanceCents || 0),
      asOfDate: rowAsOfDate,
      notes,
      agingCurrentCents: aging.agingCurrentCents,
      aging1To30Cents: aging.aging1To30Cents,
      aging31To60Cents: aging.aging31To60Cents,
      aging61To90Cents: aging.aging61To90Cents,
      agingOver90Cents: aging.agingOver90Cents,
      hasAging: aging.hasAging,
    };

    if (balanceCents === null) {
      invalidRows.push({ ...baseRow, status: 'invalid', reason: 'Missing or invalid balance amount.' });
      return;
    }

    const assessmentMatch = getUniqueCandidate(byAssessment, sourceAssessmentCode);
    const addressMatch = getUniqueCandidate(byAddress, normalizeAddress(sourceAddress));
    const uniqueCandidates = [assessmentMatch, addressMatch]
      .filter((match) => match.state === 'unique')
      .map((match) => match.property);
    const candidateIds = new Set(uniqueCandidates.map((property) => property.id));

    if (candidateIds.size > 1) {
      attentionRows.push({ ...baseRow, status: 'attention', reason: 'Assessment code and address point to different properties.' });
      return;
    }

    if (assessmentMatch.state === 'ambiguous' || addressMatch.state === 'ambiguous') {
      attentionRows.push({ ...baseRow, status: 'attention', reason: 'More than one active property matches this identifier.' });
      return;
    }

    const property = uniqueCandidates[0];
    if (property) {
      readyRows.push({
        ...baseRow,
        ...toPropertySummary(property),
        status: 'ready',
        matchOrigin: 'deterministic',
        matchType: assessmentMatch.state === 'unique' ? 'assessment code' : 'address',
      });
      return;
    }

    const normalizedName = normalizeOwnerName(sourceOwnerName);
    const savedMapping = normalizedName ? nameMappings.get(normalizedName) : null;
    const mappedProperty = savedMapping ? byId.get(savedMapping.property_id) : null;
    if (mappedProperty) {
      readyRows.push({
        ...baseRow,
        ...toPropertySummary(mappedProperty),
        status: 'ready',
        matchOrigin: 'memory',
        matchType: 'saved mapping',
        confidence: savedMapping.confidence,
      });
      return;
    }

    if (normalizedName) {
      aiPendingRows.push(baseRow);
      return;
    }

    attentionRows.push({ ...baseRow, status: 'attention', reason: 'No identifier matched: add an assessment code, address, or owner name.' });
  });

  const aiRows = [];
  if (aiPendingRows.length > 0) {
    let aiMatches = new Map();
    let aiFailure = '';
    try {
      const uniqueNames = [...new Set(aiPendingRows.map((row) => row.sourceOwnerName))];
      aiMatches = await requestAiNameMatches({ hoaId, names: uniqueNames });
    } catch (error) {
      console.warn('AI name matching unavailable:', error?.message || error);
      aiFailure = 'AI matcher unavailable — assign this owner manually.';
    }

    aiPendingRows.forEach((row) => {
      const match = aiMatches.get(row.sourceOwnerName);
      const property = match?.matched_property_id ? byId.get(match.matched_property_id) : null;
      if (property && (match.confidence || 0) >= AI_MIN_CONFIDENCE) {
        aiRows.push({
          ...row,
          ...toPropertySummary(property),
          status: 'ai',
          matchOrigin: 'ai',
          matchType: 'AI match',
          confidence: match.confidence,
          reasoning: match.reasoning || '',
        });
        return;
      }

      attentionRows.push({
        ...row,
        status: 'attention',
        reason: aiFailure || match?.reasoning || 'No confident match for this name.',
      });
    });
  }

  return {
    fileName: file.name,
    totalRows: records.length - skippedArtifactRows,
    skippedArtifactRows,
    readyRows,
    aiRows,
    attentionRows,
    invalidRows,
    properties,
    asOfDate: fallbackAsOfDate,
    imported: 0,
  };
};

// Applies confirmed rows: upserts balances (with aging), saves newly confirmed
// name mappings to memory, and writes one balance_uploads audit record.
// Each row: { propertyId, balanceCents, aging*, asOfDate, notes,
//             sourceOwnerName, matchOrigin: deterministic|memory|ai|manual, confidence }
export const applyBalanceImport = async ({ hoaId, fileName, asOfDate, rows, totalRows, skippedRows = 0 }) => {
  if (!hoaId) throw new Error('Missing HOA id.');

  const rowsToImport = (rows || []).filter((row) => row?.propertyId);
  if (rowsToImport.length === 0) {
    throw new Error('No confirmed balance rows are ready to import.');
  }

  const propertyIdCounts = new Map();
  rowsToImport.forEach((row) => {
    propertyIdCounts.set(row.propertyId, (propertyIdCounts.get(row.propertyId) || 0) + 1);
  });
  const duplicates = [...propertyIdCounts.values()].filter((count) => count > 1);
  if (duplicates.length > 0) {
    throw new Error('Two or more confirmed rows point to the same property. Fix the assignments before importing.');
  }

  const { data: authData } = await supabase.auth.getUser();
  const importedBy = authData?.user?.id || null;
  const nowIso = new Date().toISOString();
  const resolvedFileName = fileName || 'balance-import.csv';

  const balanceRecords = rowsToImport.map((row) => ({
    hoa_id: hoaId,
    property_id: row.propertyId,
    balance_cents: row.balanceCents,
    as_of_date: row.asOfDate || asOfDate || null,
    source_filename: resolvedFileName,
    notes: row.notes || null,
    imported_by: importedBy,
    imported_at: nowIso,
    updated_at: nowIso,
    aging_current_cents: row.hasAging ? (row.agingCurrentCents ?? null) : null,
    aging_1_30_cents: row.hasAging ? (row.aging1To30Cents ?? null) : null,
    aging_31_60_cents: row.hasAging ? (row.aging31To60Cents ?? null) : null,
    aging_61_90_cents: row.hasAging ? (row.aging61To90Cents ?? null) : null,
    aging_over_90_cents: row.hasAging ? (row.agingOver90Cents ?? null) : null,
  }));

  const { error: upsertError } = await supabase
    .from('property_balances')
    .upsert(balanceRecords, { onConflict: 'hoa_id,property_id' });

  if (upsertError) throw upsertError;

  // Save newly confirmed mappings so these names never need review again.
  const mappingRecords = [];
  const seenNames = new Set();
  rowsToImport.forEach((row) => {
    if (row.matchOrigin !== 'ai' && row.matchOrigin !== 'manual') return;
    const normalizedName = normalizeOwnerName(row.sourceOwnerName);
    if (!normalizedName || seenNames.has(normalizedName)) return;
    seenNames.add(normalizedName);
    mappingRecords.push({
      hoa_id: hoaId,
      source_name: String(row.sourceOwnerName).trim(),
      normalized_name: normalizedName,
      property_id: row.propertyId,
      match_source: row.matchOrigin === 'ai' ? 'ai_confirmed' : 'manual',
      confidence: typeof row.confidence === 'number' ? Math.round(row.confidence * 100) / 100 : null,
      confirmed_by: importedBy,
      confirmed_at: nowIso,
    });
  });

  let newMappingsSaved = 0;
  if (mappingRecords.length > 0) {
    const { error: mappingError } = await supabase
      .from('balance_name_mappings')
      .upsert(mappingRecords, { onConflict: 'hoa_id,normalized_name' });

    if (mappingError) {
      console.warn('Name mappings could not be saved:', mappingError?.message || mappingError);
    } else {
      newMappingsSaved = mappingRecords.length;
    }
  }

  const counts = {
    deterministic: rowsToImport.filter((row) => row.matchOrigin === 'deterministic').length,
    memory: rowsToImport.filter((row) => row.matchOrigin === 'memory').length,
    ai: rowsToImport.filter((row) => row.matchOrigin === 'ai').length,
    manual: rowsToImport.filter((row) => row.matchOrigin === 'manual').length,
  };

  try {
    const { error: auditError } = await supabase
      .from('balance_uploads')
      .insert({
        hoa_id: hoaId,
        uploaded_by: importedBy,
        filename: resolvedFileName,
        as_of_date: asOfDate || null,
        total_rows: totalRows || rowsToImport.length,
        imported_rows: rowsToImport.length,
        memory_matched_rows: counts.memory,
        ai_matched_rows: counts.ai,
        manual_matched_rows: counts.manual,
        skipped_rows: skippedRows,
        new_mappings_saved: newMappingsSaved,
      });

    if (auditError) throw auditError;
  } catch (error) {
    console.warn('Balance upload audit record failed:', error?.message || error);
  }

  return {
    imported: rowsToImport.length,
    newMappingsSaved,
    skipped: skippedRows,
    counts,
  };
};

// Back-compat wrapper: imports only rows that need no review.
export const importPropertyBalancesCsv = async ({ hoaId, file, preview, asOfDate }) => {
  const importPreview = preview || await analyzePropertyBalancesCsv({ hoaId, file, asOfDate });
  const result = await applyBalanceImport({
    hoaId,
    fileName: importPreview.fileName || file?.name,
    asOfDate: importPreview.asOfDate || parseAsOfDate(asOfDate),
    rows: importPreview.readyRows || [],
    totalRows: importPreview.totalRows,
  });

  return {
    ...result,
    readyRows: importPreview.readyRows || [],
    attentionRows: importPreview.attentionRows || [],
    invalidRows: importPreview.invalidRows || [],
    totalRows: importPreview.totalRows || result.imported,
  };
};

export const getLatestBalanceUpload = async (hoaId) => {
  if (!hoaId) return null;
  try {
    const { data, error } = await supabase
      .from('balance_uploads')
      .select('id, filename, as_of_date, total_rows, imported_rows, memory_matched_rows, ai_matched_rows, manual_matched_rows, skipped_rows, new_mappings_saved, uploaded_at')
      .eq('hoa_id', hoaId)
      .order('uploaded_at', { ascending: false })
      .limit(1);

    if (error) throw error;
    return data?.[0] || null;
  } catch (error) {
    console.warn('Latest balance upload unavailable:', error?.message || error);
    return null;
  }
};

export const formatCurrency = (amount = 0) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(amount);
